package ai.wagemix

import kotlin.collections.set
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min

private const val DEFAULT_WEIGHT = 100F

// TODO(ryan): in order to make disabling/enabling items work symmetrically
//  across multiple operations, we need to store whether the item was locked by
//  the user or by means of disabling the item. Then we will have enough memory
//  when recalculating weights to know whether to preserve the locked state or
//  not

data class WeightData(
    val itemId: Int,
    val weight: Float = 100F,
    val isLocked: Boolean = false,
    val isEnabled: Boolean = true
) {
    val isUnlocked: Boolean
        get() = !isLocked
    val isDisabled: Boolean
        get() = !isEnabled

    fun atScale(scale: Double): WeightData = if (scale == 1.0) this else withWeight((weight * scale).toFloat())
    fun disabled(): WeightData = if (isDisabled) this else copy(isEnabled = false, isLocked = true)
    fun enabled(): WeightData = if (isEnabled) this else copy(isEnabled = true)
    fun locked(): WeightData = if (isLocked) this else copy(isLocked = true)
    fun unlocked(): WeightData = if (isUnlocked) this else copy(isLocked = false)
    fun withWeight(newWeight: Float): WeightData = copy(weight = newWeight)
    fun zeroed(): WeightData = if (weight == 0F) this else withWeight(0F)
}

fun recalculateWeightsGivenEnabledItem(itemId: Int, initialMap: Map<Int, WeightData>): Map<Int, WeightData> {
    if (initialMap.isEmpty()) {
        return mapOf(itemId to WeightData(itemId = itemId, weight = DEFAULT_WEIGHT))
    }

    val match = initialMap[itemId]
    if (match?.isEnabled == true) {
        // If the item is already enabled, no need to recalculate
        return initialMap
    }

    val ret = initialMap.toMutableMap()
    if (match != null) {
        ret[itemId] = match.enabled()
        if (ret.size == 1) {
            return ret
        }
    } else {
        ret[itemId] = WeightData(itemId = itemId, weight = DEFAULT_WEIGHT)
    }

    val initialTotalWeightSpace = initialMap.count { (_, data) -> data.isEnabled } * DEFAULT_WEIGHT.toDouble()  // <-- available weight when starting
    val resultCount = ret.count { (_, data) -> data.isEnabled }
    val resultTotalWeightSpace = resultCount * DEFAULT_WEIGHT.toDouble()          // <-- available weight after the item is removed or disabled
    val weightSpaceRatio = resultTotalWeightSpace / initialTotalWeightSpace

    // First scale the weights of all the locked items by the weight space ratio
    // keeping track of the total locked weight (of the enabled items). The
    // unlocked items will be apportioned the remaining weight space. Of note is
    // that the disabled items MUST have their calculations performed as though
    // they were enabled so that they retain a memory of their previous weight
    // despite this and any future changes.
    var totalLockedWeight = 0.0
    ret.keys.forEach { id ->
        ret[id]?.let { currentData ->
            if (currentData.isLocked) {
                val newWeight = currentData.weight * weightSpaceRatio
                ret[id] = currentData.withWeight(newWeight.toFloat())
                if (currentData.isEnabled) {
                    totalLockedWeight += newWeight
                }
            }
        }
    }

    // Because of disabling/enabling weights, it is possible that we could end
    // up exceeding our total weight space by reenabling a previously disabled
    // item. In this case, we need to distribute the breaking of the lock
    // guarantee across all items that were not the reenabled item.
    if (totalLockedWeight > resultTotalWeightSpace) {
        // If the total locked weight exceeds the result total weight space, we
        // need to scale down the locked weights to fit within the available space.
        val availableSpace = resultTotalWeightSpace - (ret[itemId]?.weight?.toDouble() ?: 0.0)
        val scaleFactor = availableSpace / resultTotalWeightSpace
        ret.keys.filter { it != itemId }.forEach { id ->
            ret[id]?.let { currentData ->
                if (currentData.isLocked && currentData.isEnabled) {
                    ret[id] = currentData.atScale(scaleFactor)
                }
            }
        }
        totalLockedWeight = resultTotalWeightSpace // <-- now we have a valid total locked weight
    }

    // the remaining space is the space we're mapping to that must be covered
    val remainingWeightSpaceAfterLockedCalc = resultTotalWeightSpace - totalLockedWeight
    val initialUnlockedTotalWeight = initialMap.values.filter { it.isEnabled && it.isUnlocked }.sumOf { it.weight.toDouble() }
    // There are two different paths--one in which there was already a match,
    // and the other in which there is a new item. The difference in the logic
    // is that when there is a match, we're restoring the previous state,
    // whereas when there is a new item, we are distributing the weight
    // proportionally to the existing unlocked items.
    if (match == null) {
        // The remaining space needs to be split between the new item and the
        // preexisting items proportionally to their previous weights.
        val unlockedItemCount = ret.count { (_, data) -> data.isEnabled && data.isUnlocked }
        val newItemWeight = remainingWeightSpaceAfterLockedCalc / unlockedItemCount
        ret[itemId] = WeightData(itemId = itemId, weight = newItemWeight.toFloat())
        val remainingWeightSpace = remainingWeightSpaceAfterLockedCalc - newItemWeight
        ret.keys.filter { it != itemId }.forEach { id ->
            ret[id]?.let { currentData ->
                if (currentData.isUnlocked) {
                    val previousRatio = currentData.weight / initialUnlockedTotalWeight
                    val newWeight = previousRatio * remainingWeightSpace
                    ret[id] = currentData.withWeight(newWeight.toFloat())
                }
            }
        }
    } else {
        val remainingWeightSpaceScale = when (initialUnlockedTotalWeight <= 0.0) {
            true -> 0.0
            false -> remainingWeightSpaceAfterLockedCalc / initialUnlockedTotalWeight
        }
        ret.keys.forEach { id ->
            ret[id]?.let { currentData ->
                if (currentData.isUnlocked) {
                    ret[id] = currentData.atScale(remainingWeightSpaceScale)
                }
            }
        }
    }
    return ret
}

fun recalculateWeightsGivenDisabledItem(
    itemIdToDisable: Int,
    remove: Boolean,
    initialMap: Map<Int, WeightData>
): Map<Int, WeightData> {
    if (initialMap.size == 1) {
        // If there is only one item, disabling it or removing it will leave an
        // empty map. We wish to avoid this.
        return initialMap
    }

    val match = initialMap[itemIdToDisable] ?: return initialMap
    if (match.isDisabled && !remove) {
        return initialMap // No need to recalculate if the item is already disabled
    }

    val ret = initialMap.toMutableMap()
    if (remove) {
        ret.remove(itemIdToDisable)
    } else {
        // <-- a disabled item must always be locked so that the previous
        // setting is preserved
        ret[itemIdToDisable] = match.disabled().locked()
    }

    if (ret.values.all { it.isDisabled }) {
        return initialMap   // <-- should not be able to disable all items
    }

    val initialTotalWeightSpace = initialMap.count { (_, data) -> data.isEnabled } * 100.0  // <-- available weight when starting
    val resultTotalWeightSpace = ret.count { (_, data) -> data.isEnabled } * 100.0          // <-- available weight after the item is removed or disabled
    // we know that initialTotalWeightSpace is never 0 because we have at least one item
    val weightSpaceRatio = resultTotalWeightSpace / initialTotalWeightSpace                             // <-- used to scale the locked items

    // First scale the weights of all the locked items by the weight space ratio
    // keeping track of the total locked weight (of the enabled items). The
    // unlocked items will be apportioned the remaining weight space. Of note is
    // that the disabled items MUST have their calculations performed as though
    // they were enabled so that they retain a memory of their previous weight
    // despite this and any future changes.
    var totalLockedWeight = 0.0
    ret.keys.forEach { id ->
        ret[id]?.let { currentData ->
            if (currentData.isLocked) {
                val newWeight = currentData.weight * weightSpaceRatio
                ret[id] = currentData.withWeight(newWeight.toFloat())
                if (currentData.isEnabled) {
                    totalLockedWeight += newWeight
                }
            }
        }
    }

    // the remaining space is the space we're mapping to that must be covered
    val remainingWeightSpaceAfterLockedCalc = resultTotalWeightSpace - totalLockedWeight
    // Note: unlocked items cannot be disabled
    val initialUnlockedTotalWeight = initialMap.values.filter { it.isUnlocked }.sumOf { it.weight.toDouble() }
    // if the item being removed/disabled was unlocked, we cannot count this as part of the initial unlocked weight
    val initialAdjustedUnlockedWeight = initialUnlockedTotalWeight - (if (match.isUnlocked) match.weight else 0F).toDouble()
    val remainingWeightSpaceScale = when (initialAdjustedUnlockedWeight <= 0.0) {
        true -> 0.0
        false -> remainingWeightSpaceAfterLockedCalc / initialAdjustedUnlockedWeight
    }
    ret.keys.forEach { id ->
        ret[id]?.let { currentData ->
            if (currentData.isUnlocked) {
                ret[id] = currentData.atScale(remainingWeightSpaceScale)
            }
        }
    }
    return ret
}

fun recalculateWeightsGivenWeightChange(
    itemId: Int,
    newWeight: Float,
    map: MutableMap<Int, WeightData>,
) {
    if (itemId !in map.keys) {
        return // No need to recalculate if the item does not exist
    }

    val prev = map[itemId] ?: return
    if (prev.isLocked || prev.isDisabled) {
        // there is nothing to do if the item is locked or disabled
        return
    }
    val enabledValueCount = map.filterValues { it.isEnabled && it.isUnlocked }.count()
    if (enabledValueCount < 2) {
        // If there is only one enabled item, we cannot recalculate weights
        // because there is no other item to distribute the weight to.
        return
    }

    val totalWeightSpace = map.count { (_, data) -> data.isEnabled } * DEFAULT_WEIGHT.toDouble()
    val totalLockedWeight = map.values.filter { it.isLocked && it.isEnabled }.sumOf { it.weight.toDouble() }
    val totalUnlockedWeight = totalWeightSpace - totalLockedWeight
    if (totalUnlockedWeight <= 0) {
        // If there are no unlocked weights, we cannot recalculate
        return
    }

    val newWeight = max(0.0, min(totalUnlockedWeight, newWeight.toDouble()))
    // the diff is the amount increase/decrease that the item is changing by.
    // We will use this to add to/remove from the other unlocked weights.
    val availableWeight = totalUnlockedWeight - newWeight
    map.keys.filter { it != itemId }.forEach { id ->
        map[id]?.let { currentData ->
            if (currentData.isUnlocked && currentData.isEnabled) {
                val denominator = totalUnlockedWeight - prev.weight.toDouble()
                if (denominator == 0.0) {
                    val diff = abs(newWeight - prev.weight.toDouble())
                    val newWeight = diff / (enabledValueCount - 1)
                    map[id] = currentData.withWeight(newWeight.toFloat())
                } else {
                    val pct = currentData.weight / denominator
                    val newWeight = availableWeight * pct
                    map[id] = currentData.withWeight(newWeight.toFloat())
                }
            }
        }
    }
    map[itemId] = prev.withWeight(newWeight.toFloat())
}